import { FC, useMemo } from 'react';

import { FormFieldType, TCustomField } from 'src/types';

import CheckGroupField from './CheckGroupField';
import DropdownField from './DropdownField';
import RadioField from './RadioField';
import FileUploadField from './FileUploadField';
import SingleInstance from './SingleInstance';
import TextInputField from './TextInputField';
import MultiInstance from './MultiInstance';
import { FormGroupProps } from './FormGroup';

interface FormFieldProps extends Pick<FormGroupProps, 'onUploadFile'> {
  field: TCustomField;
  required?: boolean;
  readOnly?: boolean;
  showManualError?: boolean;
  variant?: 'standard' | 'outlined';
}

const FormField: FC<FormFieldProps> = ({ field, readOnly, required, showManualError, variant, onUploadFile }) => {
  const isRequired = required && field.required;
  const isReadOnly = readOnly || field.readOnly;

  const textInputFieldType = useMemo(() => {
    switch (field.type) {
      case FormFieldType.DATE:
        return 'date';
      case FormFieldType.PASSWORD:
        return 'password';
      case FormFieldType.NUMBER:
        return 'number';
      case FormFieldType.EMAIL:
        return 'email';
      default:
        return 'text';
    }
  }, [field.type]);

  switch (field.type) {
    case FormFieldType.SELECT:
    case FormFieldType.MULTI_SELECT:
      return (
        <DropdownField
          name={field.path}
          label={field.label}
          readOnly={isReadOnly}
          required={isRequired}
          options={field.options || []}
          variant={field.variant || variant || 'standard'}
          placeholder={field.placeholder}
          multiple={field.type === FormFieldType.MULTI_SELECT}
        />
      );

    case FormFieldType.SINGLE_INSTANCE:
      return (
        <SingleInstance
          label={field.label}
          path={field.path}
          properties={field.properties || []}
          readOnly={isReadOnly}
          required={isRequired}
          showManualError={showManualError}
          variant={variant}
          validator={field.validator}
        />
      );

    case FormFieldType.MULTI_INSTANCE:
      return (
        <MultiInstance
          addButton={field.addButton}
          comment={field.comment}
          deleteButton={field.deleteButton}
          label={field.label}
          path={field.path}
          maxEntries={field.maxEntries}
          readOnly={isReadOnly}
          required={isRequired}
          variant={variant}
          properties={field.properties || []}
        />
      );

    case FormFieldType.RADIO_GROUP:
      return (
        <RadioField
          comment={field.comment}
          name={field.path}
          label={field.label}
          readOnly={isReadOnly}
          required={isRequired}
          options={field.options || []}
          validator={field.validator}
          showManualError={showManualError}
        />
      );

    case FormFieldType.CHECK_GROUP:
      return (
        <CheckGroupField
          name={field.path}
          label={field.label}
          readOnly={isReadOnly}
          required={isRequired}
          layout={field.optionsLayout}
          options={field.options || []}
          validator={field.validator}
          showManualError={showManualError}
        />
      );

    case FormFieldType.FILE_UPLOAD:
    case FormFieldType.LOCAL_FILE_UPLOAD:
      return (
        <FileUploadField
          acceptFileTypes={field.acceptFileTypes}
          label={field.label}
          path={field.path}
          required={field.required}
          uploadButton={field.uploadButton}
          readOnly={isReadOnly}
          onUploadFile={onUploadFile}
        />
      );

    case FormFieldType.TEXT:
    case FormFieldType.DATE:
    case FormFieldType.EMAIL:
    case FormFieldType.PASSWORD:
    case FormFieldType.NUMBER:
    default:
      return (
        <TextInputField
          disabled={field.disabled}
          label={field.label}
          comment={field.comment}
          maxLength={field.maxLength}
          multiple={field.multiple}
          name={field.path}
          readOnly={isReadOnly}
          required={isRequired}
          placeholder={field.placeholder}
          type={textInputFieldType}
          step={field.step}
          validator={field.validator || { required: isRequired }}
        />
      );
  }
};

export default FormField;
